/**
 * Rendering styles offered by the Enhance panel.
 *
 * A preset is *only* a set of prompt fragments. The scene manifest stays style-agnostic, so the same
 * botanical facts drive a photoreal render and an anime one — swapping style never risks changing
 * which plants the model thinks are in the frame.
 */

export interface StylePreset {
  id: string;
  label: string;
  /** One-line description for the picker. */
  blurb: string;
  /** Opening clause: what kind of image this is. */
  styleClause: string;
  /** Optics, materials, and surface treatment. */
  cameraClause: string;
  /** Things this style specifically must avoid. */
  negativeClause: string;
}

export const STYLE_PRESETS: readonly StylePreset[] = [
  {
    id: 'photoreal',
    label: 'Photoreal',
    blurb: 'A real photograph of the garden as designed.',
    styleClause:
      'Render this as a photorealistic landscape photograph of a real, built garden. Every plant must read as living botanical material — individual leaves, believable bark texture, natural growth irregularity — not as CGI or a 3D model.',
    cameraClause:
      'Full-frame DSLR, 35mm lens, f/5.6, natural depth of field with a slightly soft background. Physically accurate light transport: dappled light through canopies, soft contact shadows where stems meet soil, subtle subsurface scattering in backlit leaves. Real ground: render whichever surfaces the ground is actually made of at photographic fidelity — turf with its own grain, mulch and soil with fine particle texture and leaf litter, water with true reflection and refraction.',
    negativeClause:
      'No CGI sheen, no plastic or waxy surfaces, no uniform repeated leaf instances, no video-game grass cards, no HDR halos, no oversaturation.',
  },
  {
    id: 'golden-hour',
    label: 'Golden hour photo',
    blurb: 'Photoreal, pushed to warm low-angle light.',
    styleClause:
      'Render this as a photorealistic landscape photograph taken during golden hour. Keep every plant botanically real and exactly where it stands.',
    cameraClause:
      'Warm low-angle sunlight, long raking shadows across the ground, rim light catching the edges of foliage and seed heads, gentle atmospheric haze with depth. 50mm lens, f/4, shallow falloff into the background.',
    negativeClause:
      'No lens flare artefacts over the subjects, no orange colour cast crushing the foliage hues, no blown highlights.',
  },
  {
    id: 'architectural',
    label: 'Architectural visualisation',
    blurb: 'Clean, professional practice-quality presentation render.',
    styleClause:
      'Render this as a high-end landscape architecture visualisation of the kind a design practice would present to a client. Photoreal materials with slightly idealised, immaculately maintained planting.',
    cameraClause:
      'Even, controlled lighting with clean soft shadows. Crisp focus front to back, corrected verticals, calm neutral colour grade. Immaculate bed edges, tidy mulch, crisp clean margins wherever one ground surface meets another — turf, paving, gravel and water each impeccably kept and each unmistakably itself.',
    negativeClause:
      'No people, no vehicles, no signage, no watermarks, no text, no annotation callouts, no scale figures.',
  },
  {
    id: 'watercolour',
    label: 'Watercolour rendering',
    blurb: 'Hand-painted landscape plan illustration.',
    styleClause:
      'Render this as a hand-painted watercolour landscape illustration in the tradition of a landscape architect\'s presentation drawing. Loose, confident washes with visible pigment granulation and paper tooth.',
    cameraClause:
      'Wet-on-wet blooms in the canopies, dry-brush texture on trunks and grasses, light pencil under-drawing showing at the edges, generous untouched white paper in the sky and along highlights. Colours layered in transparent glazes rather than blocked in flat.',
    negativeClause:
      'No hard digital outlines, no gradient banding, no photographic detail, no uniform vector fills.',
  },
  {
    id: 'anime',
    label: 'Anime',
    blurb: 'Cel-shaded background art, Makoto Shinkai register.',
    styleClause:
      'Render this as a high-quality anime background painting — the lush, luminous style of a modern Japanese animated film\'s establishing shot.',
    cameraClause:
      'Cel-shaded foliage grouped into readable painted clusters rather than individual leaves, crisp light shafts, saturated skies with volumetric clouds, glowing rim light on foliage edges, painterly ground with clear value separation. Clean line economy, high colour saturation, gentle bloom.',
    negativeClause:
      'No characters, no text, no speech bubbles, no photographic texture, no muddy desaturated colour.',
  },
  {
    id: 'storybook',
    label: 'Storybook diorama',
    blurb: 'Cosy, hand-crafted miniature — the Tiny Glade look.',
    styleClause:
      'Render this as a cosy, hand-crafted miniature diorama of the garden — soft, stylised, storybook charm with a gently toy-like sense of scale.',
    cameraClause:
      'Soft rounded forms, warm inviting palette, gentle ambient occlusion nestling plants into the ground, subtle tilt-shift miniature falloff, felt-and-clay material feel with a light hand-painted texture. Cheerful bounce light, no harsh shadows.',
    negativeClause:
      'No photographic realism, no harsh contrast, no grim or desaturated colour, no text.',
  },
  {
    id: 'winter-dusk',
    label: 'Nocturne',
    blurb: 'Evening scene with low ambient light.',
    styleClause:
      'Render this as an atmospheric evening photograph of the garden just after sunset, with the planting reading in silhouette and half-light.',
    cameraClause:
      'Deep blue ambient sky gradient, warm accent uplighting grazing a few trunks and canopies, long exposure smoothness, luminous residual glow on the western horizon. 35mm lens, f/2.8, gentle grain.',
    negativeClause:
      'No daylight, no midday shadows, no flat even illumination, no text.',
  },
];

export const DEFAULT_STYLE_PRESET_ID = 'anime';

export function getStylePreset(id: string): StylePreset {
  return (
    STYLE_PRESETS.find((preset) => preset.id === id) ??
    (STYLE_PRESETS.find((preset) => preset.id === DEFAULT_STYLE_PRESET_ID) as StylePreset)
  );
}
